import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { EVENT_TYPE_LABELS, EVENT_TYPE_OPTIONS } from "../../shared/event-types";
import { clientPhaseLabel, eventCardHref } from "../../shared/inquiry";
import { STAGE_LABELS, canReadFinancials, type SalesStage } from "../../shared/permissions";
import { formatMoney } from "../../shared/money";
import { api } from "../api";
import { useSession } from "../App";
import { Banner, messageOf } from "./ui";

type EventRow = {
  id: string;
  title: string;
  sales_stage: SalesStage;
  start_date: string | null;
  date_label: string | null;
  adults: number | null;
  children: number | null;
  area: string | null;
  budget_minor?: number | null;
  next_action: string | null;
  owner_name: string | null;
  event_type: string | null;
  venue_id: string | null;
  venue_name: string | null;
  workbook_band: number | null;
  card_step: number | null;
};

const TYPES = [
  ["", "Ολοι οι τύποι"],
  ...EVENT_TYPE_OPTIONS,
  ["other", "Αλλο"],
  ["unknown", "Χωρίς τύπο"],
] as const;

const VENUES = [
  ["", "Ολοι οι χώροι"],
  ["imp_ven_casa", "Casa 28"],
  ["imp_ven_regina", "Regina 28"],
  ["imp_ven_kthma", "Κτήμα 28"],
] as const;

const BANDS = [
  ["", "Ολες οι φάσεις"],
  ["1", "Πρώτη επαφή"],
  ["2", "Πωλήσεις"],
  ["3", "Εκκρεμότητες προκαταβολής"],
  ["4", "Service"],
] as const;

const TYPE_LABEL = EVENT_TYPE_LABELS;

const STAGES = [["", "Ολα τα στάδια"], ...Object.entries(STAGE_LABELS)] as const;
const WHEN = [
  ["", "Νέες"],
  ["old", "Παλιές"],
] as const;
const PAGE = 50;

export function PipelinePage() {
  const [params, setParams] = useSearchParams();
  const { user } = useSession();
  const [events, setEvents] = useState<EventRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const requestId = useRef(0);
  const stage = params.get("stage") ?? "";
  const type = params.get("type") ?? "";
  const venue = params.get("venue") ?? "";
  const band = params.get("band") ?? "";
  const overdue = params.get("overdue") ?? "";
  const upcoming = params.get("upcoming") ?? "";
  const when = params.get("when") === "old" ? "old" : "";
  const financials = canReadFinancials(user.role, user.permissions);

  function choose(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  }

  function queryFor(offset: number): string {
    const query = new URLSearchParams();
    if (stage) query.set("stage", stage);
    if (type) query.set("type", type);
    if (venue) query.set("venue", venue);
    if (band) query.set("band", band);
    if (overdue) query.set("overdue", "1");
    if (upcoming) query.set("upcoming", "1");
    query.set("when", when === "old" ? "old" : "new");
    query.set("limit", String(PAGE));
    query.set("offset", String(offset));
    return query.toString();
  }

  useEffect(() => {
    const id = ++requestId.current;
    setEvents(null);
    setTotal(0);
    setError(null);
    api<{ events: EventRow[]; total: number }>(`/api/events?${queryFor(0)}`)
      .then((result) => {
        if (id !== requestId.current) return;
        setEvents(result.events);
        setTotal(result.total);
      })
      .catch((reason: unknown) => {
        if (id !== requestId.current) return;
        setError(messageOf(reason));
      });
  }, [stage, type, venue, band, overdue, upcoming, when]);

  function loadMore() {
    if (!events || loadingMore || events.length >= total) return;
    const id = requestId.current;
    const offset = events.length;
    setLoadingMore(true);
    setError(null);
    api<{ events: EventRow[]; total: number }>(`/api/events?${queryFor(offset)}`)
      .then((result) => {
        if (id !== requestId.current) return;
        setEvents((current) => [...(current ?? []), ...result.events]);
        setTotal(result.total);
      })
      .catch((reason: unknown) => {
        if (id !== requestId.current) return;
        setError(messageOf(reason));
      })
      .finally(() => {
        if (id === requestId.current) setLoadingMore(false);
      });
  }

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Εκδηλώσεις</h1>
          <p className="lede">Η πρόταση, η πληρωμή, το στάδιο πώλησης και η κράτηση είναι διαφορετικά πράγματα. Η αποδοχή μιας πρότασης δεν κλείνει τον χώρο.</p>
        </div>
      </header>
      <Banner error={error} />
      <button className="filter-toggle" type="button" aria-expanded={filtersOpen} onClick={() => setFiltersOpen((open) => !open)}>
        <span className="sandwich" aria-hidden="true" />
        {filtersOpen ? "Κλείσιμο φίλτρων" : "Φίλτρα"}
      </button>
      <div className={`filter-bar${filtersOpen ? " open" : ""}`}>
        <FilterGroup label="Ημερομηνία" value={when} options={WHEN} onChange={(value) => choose("when", value)} />
        <FilterGroup label="Φάση" value={band} options={BANDS} onChange={(value) => choose("band", value)} />
        <FilterGroup label="Τύπος" value={type} options={TYPES} onChange={(value) => choose("type", value)} />
        <FilterGroup label="Χώρος" value={venue} options={VENUES} onChange={(value) => choose("venue", value)} />
        <FilterGroup label="Στάδιο" value={stage} options={STAGES} onChange={(value) => choose("stage", value)} />
        {overdue ? <button type="button" className="chip on" onClick={() => choose("overdue", "")}>Εκπρόθεσμα</button> : null}
        {upcoming ? <button type="button" className="chip on" onClick={() => choose("upcoming", "")}>Επόμενες 21 ημέρες</button> : null}
      </div>
      <div className="filter-toolbar">
        <FilterSelect label="Ημερομηνία" value={when} options={WHEN} onChange={(value) => choose("when", value)} />
        <FilterSelect label="Φάση" value={band} options={BANDS} onChange={(value) => choose("band", value)} />
        <FilterSelect label="Τύπος" value={type} options={TYPES} onChange={(value) => choose("type", value)} />
        <FilterSelect label="Χώρος" value={venue} options={VENUES} onChange={(value) => choose("venue", value)} />
        <FilterSelect label="Στάδιο" value={stage} options={STAGES} onChange={(value) => choose("stage", value)} />
        {overdue ? <button type="button" className="chip on" onClick={() => choose("overdue", "")}>Εκπρόθεσμα</button> : null}
        {upcoming ? <button type="button" className="chip on" onClick={() => choose("upcoming", "")}>Επόμενες 21 ημέρες</button> : null}
      </div>
      {!events && !error ? <p>Φόρτωση εκδηλώσεων…</p> : null}
      {events && events.length === 0 ? <p>Τίποτα με αυτά τα φίλτρα.</p> : null}
      <div className="pipeline-list">
        {(events ?? []).map((event) => (
          <Link className="pipeline-card" key={event.id} to={eventCardHref(event.id)}>
            <strong>{event.title} · {phaseOf(event)}</strong>
            <span className="meta">{stageOf(event)} · {typeOf(event.event_type)} · {bandOf(event.workbook_band)}</span>
            <span>{whenOf(event)} · {event.adults ?? "?"} άτομα · {whereOf(event)}{moneyOf(event, financials)}</span>
          </Link>
        ))}
      </div>
      {events && events.length > 0 ? (
        <div className="pipeline-table-wrap">
          <div className="tablenav"><span>{events.length} από {total}</span></div>
          <table className="list-table">
            <thead>
              <tr>
                <th>Ονοματεπώνυμο</th>
                <th>Βήμα</th>
                <th>Φάση</th>
                <th>Τύπος</th>
                <th>Στάδιο</th>
                <th>Ημερομηνία</th>
                <th>Ατομα</th>
                <th>Χώρος</th>
                {financials ? <th>Προϋπολογισμός</th> : null}
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id}>
                  <td className="row-title"><Link to={eventCardHref(event.id)}>{event.title}</Link></td>
                  <td>{phaseOf(event)}</td>
                  <td>{bandOf(event.workbook_band)}</td>
                  <td>{typeOf(event.event_type)}</td>
                  <td>{stageOf(event)}</td>
                  <td>{whenOf(event)}</td>
                  <td>{event.adults ?? "—"}</td>
                  <td>{whereOf(event)}</td>
                  {financials ? <td>{event.budget_minor == null ? "—" : formatMoney(event.budget_minor)}</td> : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {events && events.length > 0 ? <p className="result-count pipeline-count">{events.length} από {total}</p> : null}
      {events && events.length < total ? (
        <button className="load-more" type="button" disabled={loadingMore} onClick={loadMore}>
          {loadingMore ? "Φόρτωση…" : "Φόρτωσε περισσότερα"}
        </button>
      ) : null}
    </>
  );
}

function phaseOf(event: EventRow): string {
  return clientPhaseLabel(event.card_step, event.sales_stage);
}

function stageOf(event: EventRow): string {
  return STAGE_LABELS[event.sales_stage] ?? event.sales_stage;
}

function typeOf(value: string | null): string {
  return TYPE_LABEL[value ?? ""] ?? "Χωρίς τύπο";
}

function bandOf(band: number | null): string {
  return BANDS.find(([value]) => value === String(band ?? 1))?.[1] ?? "Πρώτη επαφή";
}

function whenOf(event: EventRow): string {
  return event.start_date || event.date_label || "Χωρίς ημερομηνία";
}

function whereOf(event: EventRow): string {
  return event.venue_name || event.area || "Χωρίς χώρο";
}

function moneyOf(event: EventRow, financials: boolean): string {
  return financials && event.budget_minor != null ? ` · ${formatMoney(event.budget_minor)}` : "";
}

function FilterGroup({ label, value, options, onChange }: { label: string; value: string; options: readonly (readonly [string, string])[]; onChange: (value: string) => void }) {
  return (
    <div className="filter-group">
      <span>{label}</span>
      <div>
        {options.map(([option, text]) => (
          <button key={option || label} type="button" className={`chip${value === option ? " on" : ""}`} aria-pressed={value === option} onClick={() => onChange(option)}>{text}</button>
        ))}
      </div>
    </div>
  );
}

function FilterSelect({ label, value, options, onChange }: { label: string; value: string; options: readonly (readonly [string, string])[]; onChange: (value: string) => void }) {
  return (
    <label>
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map(([option, text]) => <option key={option || label} value={option}>{text}</option>)}
      </select>
    </label>
  );
}
